"use client";

import { useEffect, useRef } from "react";
import { useSeoMetadata } from "@/lib/hooks/useSeoMetadata";
import { useGlobalContent } from "@/lib/context/GlobalContentProvider";
import { ISeoContent } from "@/shared/models/content/seo-content";

interface NotFoundSeoUpdaterProps {
  notFoundSeo: ISeoContent;
}

export const NotFoundSeoUpdater = ({ notFoundSeo }: NotFoundSeoUpdaterProps) => {
  const { setPageSeo } = useSeoMetadata();
  const { globalContent } = useGlobalContent();
  const hasUpdatedRef = useRef(false);

  useEffect(() => {
    // Only update once and only when global content is available
    if (!hasUpdatedRef.current && globalContent?.seo) {
      // Mark as updated to prevent multiple calls
      hasUpdatedRef.current = true;

      // Update the SEO context with 404-specific metadata
      setPageSeo(notFoundSeo);

      // Also update document title directly as a fallback
      if (typeof document !== "undefined") {
        const siteTitle = globalContent.seo.site_title || "AMA";
        const pageTitle = `${notFoundSeo.meta_title} | ${siteTitle}`;

        document.title = pageTitle;

        // Update meta description
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute("content", notFoundSeo.meta_description || "");
        }

        // Update meta robots
        const metaRobots = document.querySelector('meta[name="robots"]');
        if (metaRobots) {
          metaRobots.setAttribute("content", notFoundSeo.meta_robots || "");
        }
      }
    }
  }, [globalContent, notFoundSeo, setPageSeo]);

  return null; // This component doesn't render anything
};

export default NotFoundSeoUpdater;
